import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import CanvasScrollSequence from './CanvasScrollSequence';

/**
 * Scroll-driven reveal of the Nađi Investitora crest.
 *
 * The crest keeps its real silver→gold gradient FILL the entire time. A stroked
 * mask "draws" along the line-work, revealing that fill progressively — so there
 * is zero colour/brightness change between the first frame and the last. The
 * serif N stays in the centre the whole time while the lines draw around it.
 *
 * The section is pinned (sticky) and tall, so the draw stretches across a long
 * scroll for a slow, deliberate reveal.
 *
 * Progressive enhancement: if JS fails, the SVG can't be fetched, or the user
 * prefers reduced motion, a static gold logo is shown instead.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';
const MASK_STROKE = 16; // line thickness of the reveal "pen" (local SVG units)
const DISPLAY = '"Inter", "Helvetica", "Arial", sans-serif';

const LogoReveal: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const maskPathRef = useRef<SVGPathElement | null>(null);
  const maskLenRef = useRef<number>(0);
  const rafRef = useRef<number>(0);
  const [animated, setAnimated] = useState(false);
  // iOS Safari can't paint a seeked paused video -> blank. So: reduced-motion ->
  // static poster; touch/coarse pointer -> canvas frame-scrub; desktop -> video.
  const [bgMode] = useState<'static' | 'canvas' | 'video'>(() => {
    const mm = (q: string) => typeof window.matchMedia === 'function' && window.matchMedia(q).matches;
    if (mm('(prefers-reduced-motion: reduce)')) return 'static';
    if (mm('(pointer: coarse)')) return 'canvas';
    return 'video';
  });

  useEffect(() => {
    let cancelled = false;
    const prefersReduce =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const applyProgress = (p: number) => {
      const mask = maskPathRef.current;
      if (!mask) return;
      const clamped = Math.min(1, Math.max(0, p));
      mask.style.strokeDashoffset = String(maskLenRef.current * (1 - clamped));
    };

    // Scrub the background clip to the SAME progress that draws the logo, so the
    // bird's-eye -> front camera move advances frame-locked with the crest.
    const applyVideo = (p: number) => {
      const v = videoRef.current;
      if (!v || prefersReduce) return; // reduced motion -> leave on the poster frame
      const d = v.duration;
      if (!d || !isFinite(d) || v.readyState < 1) return; // not enough metadata to seek yet
      const t = Math.min(1, Math.max(0, p)) * d;
      if (Math.abs(v.currentTime - t) > 0.02) v.currentTime = t; // skip no-op seeks
    };

    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        const sec = sectionRef.current;
        if (!sec) return;
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const scrollable = sec.offsetHeight - vh; // total scroll distance through the pinned section
        const rect = sec.getBoundingClientRect();
        const p = scrollable > 0 ? -rect.top / scrollable : 1;
        applyProgress(p);
        applyVideo(p);
      });
    };

    // Drive both the crest mask and the video off the same scroll, independent of
    // whether the SVG fetch below succeeds.
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    // Warm the video decoder once data is in, so currentTime seeks paint instantly
    // instead of showing a stale frame on the first scrub.
    const video = videoRef.current;
    const primeVideo = () => {
      if (!video || prefersReduce) return;
      const pr = video.play();
      if (pr && typeof pr.then === 'function') pr.then(() => video.pause()).catch(() => {});
      onScroll();
    };
    if (video && !prefersReduce) {
      video.addEventListener('loadedmetadata', onScroll);
      video.addEventListener('loadeddata', primeVideo, { once: true });
    }
    onScroll();

    fetch('/logo-mark.svg')
      .then((r) => r.text())
      .then((txt) => {
        if (cancelled || !hostRef.current) return;
        hostRef.current.innerHTML = txt;
        const svg = hostRef.current.querySelector('svg');
        if (!svg) return;

        svg.removeAttribute('width');
        svg.removeAttribute('height');
        svg.style.width = '100%';
        svg.style.height = '100%';
        svg.style.display = 'block';
        svg.style.overflow = 'visible';

        // the crest line-work is the longest path; the N is shorter and stays put
        const paths = Array.from(svg.querySelectorAll('path')) as SVGPathElement[];
        if (!paths.length) return;
        let dome = paths[0];
        let maxLen = 0;
        paths.forEach((el) => {
          let len = 0;
          try {
            len = el.getTotalLength();
          } catch {
            len = 0;
          }
          if (len > maxLen) {
            maxLen = len;
            dome = el;
          }
        });

        if (prefersReduce) {
          setAnimated(true); // leave the static filled logo in place
          return;
        }

        // build a mask whose stroked copy of the crest "draws" to reveal the fill
        let defs = svg.querySelector('defs');
        if (!defs) {
          defs = document.createElementNS(SVG_NS, 'defs');
          svg.insertBefore(defs, svg.firstChild);
        }
        const mask = document.createElementNS(SVG_NS, 'mask');
        mask.setAttribute('id', 'niDrawMask');
        mask.setAttribute('maskUnits', 'userSpaceOnUse');
        mask.setAttribute('x', '-100');
        mask.setAttribute('y', '-100');
        mask.setAttribute('width', '1000');
        mask.setAttribute('height', '1000');

        const maskPath = document.createElementNS(SVG_NS, 'path');
        maskPath.setAttribute('d', dome.getAttribute('d') || '');
        const t = dome.getAttribute('transform');
        if (t) maskPath.setAttribute('transform', t);
        maskPath.setAttribute('fill', 'none');
        maskPath.setAttribute('stroke', '#fff');
        maskPath.setAttribute('stroke-width', String(MASK_STROKE));
        maskPath.setAttribute('stroke-linecap', 'round');
        maskPath.setAttribute('stroke-linejoin', 'round');
        mask.appendChild(maskPath);
        defs.appendChild(mask);

        const len = (() => {
          try {
            return maskPath.getTotalLength();
          } catch {
            return maxLen;
          }
        })();
        maskPath.style.strokeDasharray = String(len);
        maskPath.style.strokeDashoffset = String(len);
        maskPathRef.current = maskPath;
        maskLenRef.current = len;

        // keep the real gradient fill, just gate it behind the draw mask
        dome.setAttribute('mask', 'url(#niDrawMask)');

        setAnimated(true);
        onScroll();
      })
      .catch(() => {
        /* fetch failed — the static fallback <img> remains visible */
      });

    return () => {
      cancelled = true;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (video) {
        video.removeEventListener('loadedmetadata', onScroll);
        video.removeEventListener('loadeddata', primeVideo);
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0; // reset so a remount (StrictMode) isn't stuck behind a stale id
      }
    };
  }, []);

  return (
    <Box
      ref={sectionRef}
      component="section"
      sx={{
        position: 'relative',
        minHeight: { xs: '130vh', md: '175vh' }, // shorter section → quicker draw per scroll
        backgroundColor: '#070708',
        borderTop: '1px solid rgba(255,255,255,0.09)',
        borderBottom: '1px solid rgba(255,255,255,0.09)',
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 2,
          overflow: 'hidden',
        }}
      >
        {/* Background clip: bird's-eye -> front, scrubbed by scroll. Blurred + scaled
            so its edges never show and the crest stays the focal point.
            On mobile/reduced-motion: static poster (no <video>) to avoid the iOS
            blank-on-seek issue. */}
        {bgMode === 'static' && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 0,
              backgroundImage: 'url(/building-poster.jpg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              transform: 'scale(1.08)',
              filter: 'blur(3px) brightness(0.5)',
              pointerEvents: 'none',
            }}
          />
        )}
        {bgMode === 'canvas' && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 0,
              transform: 'scale(1.08)',
              filter: 'blur(3px) brightness(0.5)',
              pointerEvents: 'none',
            }}
          >
            <CanvasScrollSequence
              framePrefix="/frames/building/"
              frameCount={80}
              getProgress={() => {
                const sec = sectionRef.current;
                if (!sec) return 0;
                const vh = window.innerHeight || document.documentElement.clientHeight;
                const scrollable = sec.offsetHeight - vh;
                const rect = sec.getBoundingClientRect();
                return scrollable > 0 ? Math.min(1, Math.max(0, -rect.top / scrollable)) : 1;
              }}
            />
          </Box>
        )}
        {bgMode === 'video' && (
          <video
            ref={videoRef}
            muted
            playsInline
            preload="auto"
            poster="/building-poster.jpg"
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 0,
              transform: 'scale(1.08)', // hide soft blur edges
              filter: 'blur(3px) brightness(0.5)',
              pointerEvents: 'none',
            }}
          >
            <source src="/building-bg.mp4" type="video/mp4" />
          </video>
        )}

        {/* Vignette scrim: pushes the building back, keeps logo legible */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            pointerEvents: 'none',
            background:
              'radial-gradient(ellipse at center, rgba(7,7,8,0.30) 0%, rgba(7,7,8,0.72) 62%, rgba(7,7,8,0.92) 100%)',
          }}
        />

        {/* Foreground: the logo + wordmark, above the video and scrim */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
        <Box
          sx={{
            width: { xs: 220, md: 340 },
            height: { xs: 220, md: 340 },
            mb: 4,
            position: 'relative',
          }}
        >
          <Box ref={hostRef} aria-hidden sx={{ width: '100%', height: '100%' }} />
          {!animated && (
            <Box
              component="img"
              src="/logo.png"
              alt="Nađi Investitora"
              sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }}
            />
          )}
        </Box>

        <Typography
          sx={{
            fontFamily: DISPLAY,
            fontWeight: 800,
            color: '#FFFFFF',
            fontSize: { xs: '1.8rem', md: '2.6rem' },
            letterSpacing: '-0.5px',
            mb: 1.5,
          }}
        >
          Nađi Investitora
        </Typography>
        <Typography sx={{ color: '#9C9CA3', fontSize: { xs: '1rem', md: '1.15rem' }, maxWidth: 520, mx: 'auto' }}>
          Direktno. Bez posrednika.
        </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LogoReveal;
